/**
 * Phase 101fk (Feb 2026) — Module tablette visite unifiée.
 *
 * Route : /visites/:visit_id
 *
 * Flow par slot (5 étapes) :
 *   1. Ouverture : snapshot ERP (produit, capacité, lots en place, DLC)
 *   2. Comptage : Q_comptée AU DÉPART (avant retrait) — attendu = Q_nayax_open
 *   3. Retrait DLC : sélection lot + qté + motif → WASTE ou retour Conthey
 *   4. Chargement : sélection lot Conthey + qté (R6/R8)
 *   5. Validation : bloquée si R1/R4/R6 non respectés → PUT Nayax
 *      Q_finale = Q_comptée − Σ retraits + Σ chargements
 *
 * UI : accordéon par slot avec badge status (PENDING / IN_PROGRESS / VALIDATED / SKIPPED).
 * Journal d'audit visible en drawer latéral.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { executeOrEnqueue, drainQueue, pendingCount, startAutoDrain } from "@/lib/visitQueue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  ArrowLeft,
  ClipboardList,
  Trash2,
  Package,
  ScanLine,
  CheckCircle2,
  Loader2,
  SkipForward,
  AlertTriangle,
  FileText,
  CloudOff,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const WITHDRAWAL_REASONS = [
  { value: "DLC", label: "DLC dépassée" },
  { value: "EMBALLAGE", label: "Emballage abîmé" },
  { value: "SOUILLE", label: "Souillé" },
  { value: "RAPPEL_PRODUIT", label: "Rappel produit" },
  { value: "AUTRE", label: "Autre" },
];

const fmtDlc = (dlc) => {
  if (!dlc) return "—";
  const iso = String(dlc).slice(0, 10);
  const [y, m, d] = iso.split("-");
  return d && m ? `${d}/${m}` : iso;
};

const daysToDlc = (dlc) => {
  if (!dlc) return null;
  const iso = String(dlc).slice(0, 10);
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.floor((t - now.getTime()) / (24 * 3600 * 1000));
};

const STATUS_META = {
  PENDING: { label: "À faire", cls: "bg-[#F5F5F0] text-[#575A57] border-[#E5E5E0]" },
  IN_PROGRESS: { label: "En cours", cls: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8]" },
  VALIDATED: { label: "Validé", cls: "bg-[#EAF4EE] text-[#1F4E3D] border-[#B8D4C5]" },
  SKIPPED: { label: "Passé", cls: "bg-[#F0F0EC] text-[#8C8C88] border-[#E5E5E0]" },
};

function SlotCard({ visit, slot, onRefresh, contheyLotsCache, loadContheyLots }) {
  const [busy, setBusy] = useState(null);   // "withdraw" | "count" | "load" | "validate" | "skip"

  // Retrait
  const [withdrawLotId, setWithdrawLotId] = useState("");
  const [withdrawQty, setWithdrawQty] = useState(1);
  const [withdrawReason, setWithdrawReason] = useState("DLC");
  const [withdrawNote, setWithdrawNote] = useState("");

  // Comptage
  const [qtyCounted, setQtyCounted] = useState(slot.qty_counted ?? "");
  const [varianceReason, setVarianceReason] = useState(slot.variance_reason || "");

  // Chargement
  const [loadLotId, setLoadLotId] = useState("");
  const [loadQty, setLoadQty] = useState(1);
  const [loadNote, setLoadNote] = useState("");

  useEffect(() => {
    setQtyCounted(slot.qty_counted ?? "");
    setVarianceReason(slot.variance_reason || "");
  }, [slot.qty_counted, slot.variance_reason]);

  const isReadOnly = slot.status === "VALIDATED" || slot.status === "SKIPPED";
  const meta = STATUS_META[slot.status] || STATUS_META.PENDING;

  const distributorLots = (slot.dlc_snapshot || []).filter(
    (l) => (l.quantity || 0) > 0,
  );

  const totalWithdrawn = (slot.withdrawals || []).reduce(
    (s, w) => s + Number(w.quantity || 0),
    0,
  );
  const totalLoaded = (slot.loadings || []).reduce(
    (s, l) => s + Number(l.quantity || 0),
    0,
  );
  // Phase 101fk.4 — Q_finale = Q_comptée (au départ) - retirés + chargés
  const projectedFinal =
    (Number(qtyCounted) || 0) - totalWithdrawn + totalLoaded;

  // Attendu = ce que Nayax dit qu'il reste (avant nos opérations)
  const expectedFromNayax = Number(slot.qty_nayax_open || 0);
  const variance = (Number(qtyCounted) || 0) - expectedFromNayax;

  const submitWithdraw = async () => {
    if (!withdrawLotId) {
      toast.error("Choisis un lot à retirer");
      return;
    }
    setBusy("withdraw");
    try {
      const res = await executeOrEnqueue({
        url: `/visits/${visit.id}/slot/${slot.slot_number}/retrait`,
        method: "POST",
        body: {
          lot_id: withdrawLotId,
          quantity: Number(withdrawQty),
          reason: withdrawReason,
          note: withdrawNote,
        },
        meta: { visit_id: visit.id, slot_number: slot.slot_number, action: "withdrawal" },
      });
      if (res.queued) {
        toast.info(`Hors-ligne : retrait ${withdrawQty} mis en file (rejeu auto au retour du réseau)`);
      } else {
        toast.success(
          res.data?.dlc_expired
            ? `Retrait ${withdrawQty} → WASTE (DLC dépassée)`
            : `Retrait ${withdrawQty} → retour Conthey`,
        );
      }
      setWithdrawLotId("");
      setWithdrawQty(1);
      setWithdrawNote("");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur retrait");
    } finally {
      setBusy(null);
    }
  };

  const submitCount = async () => {
    setBusy("count");
    try {
      const res = await executeOrEnqueue({
        url: `/visits/${visit.id}/slot/${slot.slot_number}/comptage`,
        method: "POST",
        body: { qty_counted: Number(qtyCounted), variance_reason: varianceReason },
        meta: { visit_id: visit.id, slot_number: slot.slot_number, action: "counting" },
      });
      if (res.queued) toast.info("Hors-ligne : comptage mis en file");
      else toast.success("Comptage enregistré");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur comptage");
    } finally {
      setBusy(null);
    }
  };

  const submitLoad = async () => {
    if (!loadLotId) {
      toast.error("Choisis un lot Conthey à charger");
      return;
    }
    setBusy("load");
    try {
      const res = await executeOrEnqueue({
        url: `/visits/${visit.id}/slot/${slot.slot_number}/chargement`,
        method: "POST",
        body: {
          lot_id: loadLotId,
          quantity: Number(loadQty),
          note: loadNote,
        },
        meta: { visit_id: visit.id, slot_number: slot.slot_number, action: "loading" },
      });
      if (res.queued) toast.info(`Hors-ligne : chargement +${loadQty} mis en file`);
      else toast.success(`Chargement +${loadQty} enregistré`);
      setLoadLotId("");
      setLoadQty(1);
      setLoadNote("");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setBusy(null);
    }
  };

  const submitValidate = async () => {
    setBusy("validate");
    try {
      const res = await executeOrEnqueue({
        url: `/visits/${visit.id}/slot/${slot.slot_number}/validate`,
        method: "POST",
        body: {},
        meta: { visit_id: visit.id, slot_number: slot.slot_number, action: "slot_validate" },
      });
      if (res.queued) toast.info(`Hors-ligne : validation slot ${slot.slot_number} mise en file`);
      else toast.success(`Slot ${slot.slot_number} validé — Q_finale = ${projectedFinal}`);
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur validation");
    } finally {
      setBusy(null);
    }
  };

  const submitSkip = async () => {
    setBusy("skip");
    try {
      const res = await executeOrEnqueue({
        url: `/visits/${visit.id}/slot/${slot.slot_number}/skip`,
        method: "POST",
        body: {},
        meta: { visit_id: visit.id, slot_number: slot.slot_number, action: "slot_skip" },
      });
      if (res.queued) toast.info("Hors-ligne : skip mis en file");
      else toast.info("Slot passé");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur skip");
    } finally {
      setBusy(null);
    }
  };

  // Phase 101fk.4 — Rejeu manuel du push Nayax pour cette visite
  const submitRetryNayax = async () => {
    setBusy("retry_nayax");
    try {
      const { data } = await api.post(`/visits/${visit.id}/push-nayax`);
      if (data?.ok) {
        toast.success(`Nayax OK · ${data.pushed || 0} slot(s) synchronisé(s)`);
      } else {
        toast.error(`Nayax ko · ${data?.error || "erreur inconnue"}`);
      }
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur rejeu Nayax");
    } finally {
      setBusy(null);
    }
  };

  const productId = slot.product_id;
  const contheyLots = productId ? contheyLotsCache[productId] || [] : [];

  return (
    <div
      className="border border-[#E5E5E0] rounded-lg overflow-hidden bg-white shadow-sm"
      data-testid={`visit-slot-${slot.slot_number}`}
    >
      <div className="bg-[#1F4E3D] text-white px-4 py-3 flex items-center gap-3">
        <div className="text-2xl font-mono font-bold min-w-[3ch]">
          {slot.slot_number}
        </div>
        <div className="flex-1">
          <div className="font-semibold">{slot.product_name || "(slot vide)"}</div>
          <div className="text-xs opacity-80 font-mono">
            {slot.product_sku} · Cap {slot.capacity_max} · AM {slot.qty_am_open}
          </div>
        </div>
        <Badge className={`${meta.cls} border`}>{meta.label}</Badge>
      </div>

      {isReadOnly ? (
        <div className="p-4 text-sm text-[#575A57] space-y-2" data-testid={`visit-slot-${slot.slot_number}-readonly`}>
          <div>
            Q_finale envoyée à Nayax :{" "}
            <b className="text-[#1F4E3D] font-mono">{slot.qty_final ?? "—"}</b>
          </div>
          <div className="text-xs">
            Retirés : {totalWithdrawn} · Comptés : {slot.qty_counted ?? "—"} · Chargés : {totalLoaded}
          </div>
          {/* Phase 101fk.4 — Statut push Nayax + bouton rejeu */}
          {slot.status === "VALIDATED" && (
            <div className="flex items-center gap-2 flex-wrap">
              {slot.nayax_push_status === "ok" && (
                <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#B8D4C5]">
                  Nayax OK · {slot.nayax_push_at ? new Date(slot.nayax_push_at).toLocaleTimeString("fr-CH") : ""}
                </Badge>
              )}
              {slot.nayax_push_status === "queued" && (
                <Badge className="bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8] flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> En file Nayax
                </Badge>
              )}
              {(slot.nayax_push_status === "error_4xx" || slot.nayax_push_status === "error_5xx") && (
                <Badge className="bg-[#FBE9E4] text-[#7A1F1F] border-[#C05C47]/50 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Nayax ko
                  {slot.nayax_push_error && (
                    <span className="ml-1 truncate max-w-[240px]" title={slot.nayax_push_error}>· {slot.nayax_push_error}</span>
                  )}
                </Badge>
              )}
              {(slot.nayax_push_status === "queued" || slot.nayax_push_status === "error_4xx" || slot.nayax_push_status === "error_5xx") && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={submitRetryNayax}
                  disabled={busy === "retry_nayax"}
                  className="h-7 border-[#1F4E3D] text-[#1F4E3D]"
                  data-testid={`visit-slot-${slot.slot_number}-retry-nayax`}
                >
                  {busy === "retry_nayax" ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-1" />
                  )}
                  Réessayer Nayax
                </Button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 space-y-4">
          {/* Étape 2 — Comptage (AU DÉPART, avant retrait — Alain Phase 101fk.4) */}
          <section className="border border-[#E5E5E0] rounded-md p-3 bg-[#FAFAF7]" data-testid={`visit-slot-${slot.slot_number}-count`}>
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="w-4 h-4 text-[#8C6B2F]" />
              <div className="font-semibold text-sm">Comptage au départ</div>
              <div className="ml-auto text-xs text-[#575A57]">
                Nayax : <span className="font-mono">{expectedFromNayax}</span>
                {slot.qty_counted !== null && slot.qty_counted !== undefined && Math.abs(variance) > 0.001 && (
                  <span className={`ml-2 font-mono ${Math.abs(variance) > 2 ? "text-[#C05C47] font-bold" : "text-[#8C6B2F]"}`}>
                    Écart {variance > 0 ? "+" : ""}{variance}
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Input
                type="number"
                min="0"
                step="1"
                placeholder="Barquettes présentes à l'arrivée"
                value={qtyCounted}
                onChange={(e) => setQtyCounted(e.target.value)}
                className="h-9 font-mono"
                data-testid={`visit-slot-${slot.slot_number}-cqty`}
              />
              <Input
                placeholder="Motif si écart Nayax"
                value={varianceReason}
                onChange={(e) => setVarianceReason(e.target.value)}
                className="h-9"
                data-testid={`visit-slot-${slot.slot_number}-creason`}
              />
              <Button
                onClick={submitCount}
                disabled={busy === "count" || qtyCounted === ""}
                className="h-9 bg-[#8C6B2F] hover:bg-[#6D5324]"
                data-testid={`visit-slot-${slot.slot_number}-csubmit`}
              >
                {busy === "count" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </div>
          </section>

          {/* Étape 3 — Retrait DLC */}
          <section className="border border-[#E5E5E0] rounded-md p-3 bg-[#FAFAF7]" data-testid={`visit-slot-${slot.slot_number}-withdraw`}>
            <div className="flex items-center gap-2 mb-2">
              <Trash2 className="w-4 h-4 text-[#C05C47]" />
              <div className="font-semibold text-sm">Retrait DLC / défaut</div>
              {totalWithdrawn > 0 && (
                <Badge className="ml-auto bg-[#FBE9E4] text-[#7A1F1F] border-[#C05C47]/30">
                  {totalWithdrawn} retiré(s)
                </Badge>
              )}
            </div>
            {distributorLots.length === 0 ? (
              <div className="text-xs italic text-[#8C8C88]">
                Aucun lot en place dans ce slot au moment de l'ouverture.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <Select value={withdrawLotId} onValueChange={setWithdrawLotId}>
                  <SelectTrigger className="h-9 col-span-2" data-testid={`visit-slot-${slot.slot_number}-wlot`}>
                    <SelectValue placeholder="Lot à retirer" />
                  </SelectTrigger>
                  <SelectContent>
                    {distributorLots.map((l) => {
                      const d = daysToDlc(l.dlc);
                      const crit = d !== null && d <= 0;
                      return (
                        <SelectItem key={l.lot_id} value={l.lot_id}>
                          <span className="font-mono">{l.lot_number}</span>
                          {" · DLC "}{fmtDlc(l.dlc)}{" · qty "}{l.quantity}
                          {crit && <span className="text-[#C05C47] font-bold ml-1">EXPIRÉ</span>}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="0.01"
                  step="1"
                  value={withdrawQty}
                  onChange={(e) => setWithdrawQty(e.target.value)}
                  className="h-9 font-mono"
                  data-testid={`visit-slot-${slot.slot_number}-wqty`}
                />
                <Select value={withdrawReason} onValueChange={setWithdrawReason}>
                  <SelectTrigger className="h-9" data-testid={`visit-slot-${slot.slot_number}-wreason`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WITHDRAWAL_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={submitWithdraw}
                  disabled={busy === "withdraw" || !withdrawLotId}
                  className="h-9 bg-[#C05C47] hover:bg-[#A04E2C]"
                  data-testid={`visit-slot-${slot.slot_number}-wsubmit`}
                >
                  {busy === "withdraw" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Retirer"}
                </Button>
              </div>
            )}
            {(slot.withdrawals || []).length > 0 && (
              <div className="mt-2 text-xs space-y-0.5">
                {slot.withdrawals.map((w, i) => (
                  <div key={i} className="text-[#575A57] font-mono">
                    · {w.lot_number} · {w.quantity} × {w.reason} · {w.dlc_expired ? "WASTE" : "→ Conthey"}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Étape 4 — Chargement */}
          <section className="border border-[#E5E5E0] rounded-md p-3 bg-[#FAFAF7]" data-testid={`visit-slot-${slot.slot_number}-load`}>
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-[#1F4E3D]" />
              <div className="font-semibold text-sm">Chargement</div>
              {totalLoaded > 0 && (
                <Badge className="ml-auto bg-[#EAF4EE] text-[#1F4E3D] border-[#B8D4C5]">
                  {totalLoaded} chargé(s)
                </Badge>
              )}
            </div>
            {!productId ? (
              <div className="text-xs italic text-[#8C8C88]">
                Slot sans produit mappé — chargement impossible.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={() => loadContheyLots(productId)}
                      className={`h-9 col-span-2 justify-start font-mono text-xs ${!loadLotId ? "text-[#8C8C88]" : ""}`}
                      data-testid={`visit-slot-${slot.slot_number}-llot`}
                    >
                      {loadLotId ? (
                        contheyLots.find((l) => l.id === loadLotId)?.lot_number || loadLotId
                      ) : (
                        "Choisir lot Conthey…"
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0" align="start">
                    <div className="max-h-72 overflow-y-auto">
                      {contheyLots.length === 0 ? (
                        <div className="p-3 text-xs italic text-[#A3A6A3] text-center">
                          Aucun lot Conthey pour ce produit
                        </div>
                      ) : (
                        contheyLots.map((l) => {
                          const d = daysToDlc(l.dlc);
                          const crit = d !== null && d <= 3;
                          return (
                            <button
                              key={l.id}
                              type="button"
                              onClick={() => setLoadLotId(l.id)}
                              className={`w-full flex justify-between items-center px-3 py-2 text-xs hover:bg-[#F5F5F0] border-b border-[#F0F0EC] last:border-0 ${
                                loadLotId === l.id ? "bg-[#EAF4EE]" : ""
                              }`}
                              data-testid={`visit-slot-${slot.slot_number}-lopt-${l.id}`}
                            >
                              <span className="font-mono">{l.lot_number}</span>
                              <span className="text-[#575A57]">
                                DLC {fmtDlc(l.dlc)} · qty {l.quantity}
                                {crit && <span className="text-[#C05C47] font-bold ml-1">J{d >= 0 ? `+${d}` : d}</span>}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <Input
                  type="number"
                  min="0.01"
                  step="1"
                  value={loadQty}
                  onChange={(e) => setLoadQty(e.target.value)}
                  className="h-9 font-mono"
                  data-testid={`visit-slot-${slot.slot_number}-lqty`}
                />
                <Input
                  placeholder="Note"
                  value={loadNote}
                  onChange={(e) => setLoadNote(e.target.value)}
                  className="h-9"
                />
                <Button
                  onClick={submitLoad}
                  disabled={busy === "load" || !loadLotId}
                  className="h-9 bg-[#1F4E3D] hover:bg-[#173D2F]"
                  data-testid={`visit-slot-${slot.slot_number}-lsubmit`}
                >
                  {busy === "load" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Charger"}
                </Button>
              </div>
            )}
            {(slot.loadings || []).length > 0 && (
              <div className="mt-2 text-xs space-y-0.5">
                {slot.loadings.map((l, i) => (
                  <div key={i} className="text-[#575A57] font-mono">
                    · {l.lot_number} · +{l.quantity} · DLC {fmtDlc(l.dlc)}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Étape 5 — Validation */}
          <div className="flex items-center gap-2 pt-1">
            <div className="text-sm text-[#575A57]">
              Q_finale prévue :{" "}
              <b className="font-mono text-[#1F4E3D]">{projectedFinal || 0}</b>
              {" / "}
              <span className="text-[#8C8C88]">{slot.capacity_max}</span>
            </div>
            <div className="flex-1" />
            <Button
              variant="outline"
              onClick={submitSkip}
              disabled={busy === "skip"}
              className="h-9"
              data-testid={`visit-slot-${slot.slot_number}-skip`}
              title="Passer ce slot (rien à faire)"
            >
              <SkipForward className="w-4 h-4 mr-1" /> Passer
            </Button>
            <Button
              onClick={submitValidate}
              disabled={busy === "validate" || qtyCounted === "" || qtyCounted === null}
              className="h-9 bg-[#1F4E3D] hover:bg-[#173D2F]"
              data-testid={`visit-slot-${slot.slot_number}-validate`}
            >
              {busy === "validate" ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-1" />
              )}
              Valider slot
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditLogSheet({ visitId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/visits/${visitId}/audit-log`);
      setEntries(data.entries || []);
    } catch (e) {
      toast.error("Erreur journal");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Sheet onOpenChange={(o) => o && load()}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" data-testid="visit-audit-log-btn">
          <FileText className="w-4 h-4 mr-1" /> Journal
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Journal d'audit</SheetTitle>
        </SheetHeader>
        {loading ? (
          <div className="mt-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
        ) : entries.length === 0 ? (
          <div className="mt-4 text-center text-sm text-[#8C8C88]">Aucun événement</div>
        ) : (
          <ul className="mt-4 space-y-2 text-xs">
            {entries.map((e) => (
              <li key={e.id} className="border border-[#E5E5E0] rounded p-2 bg-white">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[#575A57]">{new Date(e.at).toLocaleTimeString("fr-CH")}</span>
                  <Badge className={`text-[10px] ${e.status === "success" ? "bg-[#EAF4EE] text-[#1F4E3D]" : e.status === "error" ? "bg-[#FBE9E4] text-[#7A1F1F]" : "bg-[#FBF4E9] text-[#8C6B2F]"}`}>
                    {e.action}
                  </Badge>
                  {e.slot_number && <span className="font-mono text-[#1F4E3D]">slot {e.slot_number}</span>}
                </div>
                {(e.payload && Object.keys(e.payload).length > 0) && (
                  <div className="mt-1 text-[10px] text-[#575A57] font-mono truncate">
                    {JSON.stringify(e.payload)}
                  </div>
                )}
                {e.error_message && (
                  <div className="mt-1 text-[10px] text-[#7A1F1F]">{e.error_message}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function VisitPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [contheyLotsCache, setContheyLotsCache] = useState({});
  const [queuedCount, setQueuedCount] = useState(0);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [draining, setDraining] = useState(false);

  const refreshQueueCount = useCallback(async () => {
    try {
      const n = await pendingCount(id);
      setQueuedCount(n);
    } catch {}
  }, [id]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/visits/${id}`);
      setVisit(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement visite");
    } finally {
      setLoading(false);
      refreshQueueCount();
    }
  }, [id, refreshQueueCount]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Auto-drain queue + listeners online/offline
    const stop = startAutoDrain({
      onChange: async ({ drained }) => {
        if (drained > 0) {
          toast.success(`${drained} écriture(s) synchronisée(s)`);
          await load();
        }
        refreshQueueCount();
      },
    });
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    if (typeof window !== "undefined") {
      window.addEventListener("online", goOnline);
      window.addEventListener("offline", goOffline);
    }
    return () => {
      if (typeof stop === "function") stop();
      if (typeof window !== "undefined") {
        window.removeEventListener("online", goOnline);
        window.removeEventListener("offline", goOffline);
      }
    };
  }, [load, refreshQueueCount]);

  const manualDrain = async () => {
    setDraining(true);
    try {
      const res = await drainQueue();
      if (res.drained > 0) {
        toast.success(`${res.drained} écriture(s) rejouée(s)`);
        await load();
      } else if (res.remaining > 0) {
        toast.warning(`${res.remaining} écriture(s) toujours en attente`);
      } else {
        toast.info("Aucune écriture en attente");
      }
      refreshQueueCount();
    } catch (e) {
      toast.error("Erreur rejeu file");
    } finally {
      setDraining(false);
    }
  };

  const loadContheyLots = useCallback(async (productId) => {
    if (contheyLotsCache[productId]) return;
    try {
      const { data } = await api.get(`/moma-refills/available-lots/${productId}`);
      const lots = (data.lots || []).slice().sort((a, b) =>
        (a.lot_number || "").localeCompare(b.lot_number || "", "fr", { numeric: true })
      );
      setContheyLotsCache((prev) => ({ ...prev, [productId]: lots }));
    } catch (e) {
      toast.error("Erreur lots Conthey");
    }
  }, [contheyLotsCache]);

  const counts = useMemo(() => {
    if (!visit) return { total: 0, pending: 0, validated: 0 };
    const total = (visit.slots || []).length;
    const validated = (visit.slots || []).filter((s) => s.status === "VALIDATED").length;
    const skipped = (visit.slots || []).filter((s) => s.status === "SKIPPED").length;
    const pending = total - validated - skipped;
    return { total, validated, skipped, pending };
  }, [visit]);

  const closeVisit = async () => {
    setClosing(true);
    try {
      await api.post(`/visits/${id}/close`);
      toast.success("Visite clôturée");
      navigate(-1);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "pending_slots") {
        toast.error(`${detail.slots?.length} slot(s) non validé(s) : ${(detail.slots || []).join(", ")}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur clôture");
      }
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F4E3D]" />
      </div>
    );
  }
  if (!visit) {
    return <div className="p-8 text-center text-[#C05C47]">Visite introuvable</div>;
  }

  const isClosed = visit.status === "CLOSED";
  const canClose = counts.pending === 0 && !isClosed;

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="visit-page">
      <div className="sticky top-0 z-10 bg-white border-b-2 border-[#1F4E3D] shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            data-testid="visit-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div className="flex-1 min-w-[220px]">
            <div className="font-heading text-lg font-semibold text-[#1F4E3D] font-mono">
              Visite {visit.reference}
            </div>
            <div className="text-xs text-[#575A57]">
              {visit.distributor_name} · {visit.operator_name || "—"}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs" data-testid="visit-counts">
            <Badge className="bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8]">{counts.pending} à faire</Badge>
            <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#B8D4C5]">{counts.validated} validés</Badge>
            {counts.skipped > 0 && (
              <Badge className="bg-[#F0F0EC] text-[#8C8C88] border-[#E5E5E0]">{counts.skipped} passés</Badge>
            )}
          </div>
          {/* Phase 101fk.3 — Statut hors-ligne + file d'attente */}
          {(!isOnline || queuedCount > 0) && (
            <div className="flex items-center gap-2" data-testid="visit-queue-status">
              {!isOnline && (
                <Badge className="bg-[#FBE9E4] text-[#7A1F1F] border-[#C05C47]/50 flex items-center gap-1">
                  <CloudOff className="w-3 h-3" /> Hors-ligne
                </Badge>
              )}
              {queuedCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={manualDrain}
                  disabled={draining}
                  className="h-8 border-[#8C6B2F] text-[#8C6B2F]"
                  data-testid="visit-drain-btn"
                  title="Rejouer maintenant les écritures en file"
                >
                  {draining ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-1" />
                  )}
                  {queuedCount} en file
                </Button>
              )}
            </div>
          )}
          <AuditLogSheet visitId={visit.id} />
          {!isClosed && (
            <Button
              onClick={closeVisit}
              disabled={!canClose || closing}
              className="bg-[#1F4E3D] hover:bg-[#173D2F]"
              data-testid="visit-close-btn"
              title={!canClose ? `Reste ${counts.pending} slot(s) à traiter` : "Clôturer la visite"}
            >
              {closing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Clôturer
            </Button>
          )}
          {isClosed && (
            <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#B8D4C5]">Visite clôturée</Badge>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-4 space-y-3">
        {(visit.slots || []).length === 0 ? (
          <div className="p-8 text-center text-[#8C8C88]">
            Aucun slot pour cette visite.
          </div>
        ) : (
          (visit.slots || []).map((slot) => (
            <SlotCard
              key={slot.slot_number}
              visit={visit}
              slot={slot}
              onRefresh={load}
              contheyLotsCache={contheyLotsCache}
              loadContheyLots={loadContheyLots}
            />
          ))
        )}
      </div>
    </div>
  );
}
